import React, { useCallback, useMemo, useRef, useState } from "react";
import { Cell, Label, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import {
    CONTEXT_MENU_BLOCKERS,
    isMultiSelectClick,
    mergeLongPressHandlers,
    MULTI_SELECT_HINT_TITLE,
    longPressPulseOpacity,
    useLongPressMultiSelect,
    useLongPressPulse,
} from "../utils/multiSelectGesture";
import gestureStyles from "../utils/multiSelectGesture.module.css";
import { HelpHint } from "./HelpHint";
import styles from "./DistributionChart.module.css";

export interface DistributionItem {
    label: string;
    value: number;
    percentage?: number;
    isGroupedOther?: boolean;
}

interface DistributionChartProps {
    title: string;
    helpText?: string;
    data: DistributionItem[];
    /** Currently selected/filtered value(s) — separated by valueSeparator for multi-select */
    selectedValue?: string;
    /** Separator for multi-select values (default: ",") */
    valueSeparator?: string;
    /** Called when a segment is clicked. multiSelect is true for ⌘-click or long-press. */
    onSegmentClick?: (value: string, multiSelect: boolean, event?: React.MouseEvent) => void;
    /** Legend position: 'right' (default) or 'bottom' */
    legendPosition?: "right" | "bottom";
    /** Show total in center of donut */
    showTotal?: boolean;
    /** Override the center total instead of computing from data */
    totalOverride?: number;
    /** Format segment labels for display (filter values stay as data labels) */
    formatLabel?: (label: string) => string;
    /** Normalize labels for selection matching (defaults to formatLabel or identity) */
    matchLabel?: (label: string) => string;
    /** Allow long labels to wrap instead of truncating them */
    wrapLegendLabels?: boolean;
    /** Long-tail grouping policy. Omit to show every slice. */
    grouping?: SliceGrouping;
}

/**
 * Long-tail grouping policy.
 *
 * Two bounds decide what may be hidden, so "Other" can never swallow the pie:
 *
 * - `minSlicePercent` is a minimum selectable angle. A slice worth p% of the
 *   total spans p * 3.6 degrees, so 1% is ~3.6 degrees, roughly the smallest
 *   arc a user can reliably click. Only slices below this are candidates.
 * - `maxOtherPercent` bounds the grouped residual. Candidates are restored,
 *   largest first, until "Other" fits under this share.
 */
export interface SliceGrouping {
    /** Slices below this share (%) may be grouped. Sets the smallest clickable arc. */
    minSlicePercent: number;
    /** Hard ceiling (%) on the grouped "Other" slice. */
    maxOtherPercent: number;
    /** Only group when the chart has more slices than this. */
    minSliceCount?: number;
}

/**
 * Product types are listed in a table below their chart, so a grouped value is
 * still reachable. 1% (~3.6 degrees) is the click-target floor, and "Other"
 * stays a minor slice.
 */
export const PRODUCT_TYPE_SLICE_GROUPING: SliceGrouping = {
    minSlicePercent: 1,
    maxOtherPercent: 15,
};

/**
 * Colours have no companion table, so a grouped colour is unreachable. Group
 * only once the legend itself is unusable, and then only a negligible residual.
 */
export const COLOUR_SLICE_GROUPING: SliceGrouping = {
    minSlicePercent: 0.5,
    maxOtherPercent: 2,
    minSliceCount: 24,
};

// IKEA extended palette (Communication through colour), ordered for maximum
// categorical distinction. See ikea.net digital-how-tos/communication-through-colour.
const COLORS = [
    "#0096D2", // flora-2200 blue (extended-palette-04)
    "#E52F7E", // flora-4300 pink (extended-palette-09)
    "#7AB51D", // flora-6200 green (extended-palette-15)
    "#F26A2F", // sun-6500 orange (extended-palette-12)
    "#6B3D99", // flora-3300 purple (extended-palette-07)
    "#FFAA00", // flora-5300 amber (extended-palette-11)
    "#009985", // flora-7300 teal (extended-palette-18)
    "#005980", // flora-2400 dark blue (extended-palette-05)
    "#A3ACB0", // flora-1200 grey (extended-palette-02)
    "#D7C7A2", // flora-8100 tan (extended-palette-20)
];

// Neutral fill reserved for the grouped "Other" slice. It is outside the
// categorical palette, so Other never shares a colour with a real value.
export const OTHER_SLICE_COLOR = "#CCCCCC";

/**
 * Colour for the slice at `index` of `count`. The pie is a circle, so the last
 * slice touches the first: when the palette cycle would give both the same
 * colour, the last slice takes the next palette colour instead.
 */
export function sliceColor(index: number, count: number, isGroupedOther: boolean): string {
    if (isGroupedOther) return OTHER_SLICE_COLOR;
    const paletteIndex = index % COLORS.length;
    if (index === count - 1 && index > 0 && paletteIndex === 0) return COLORS[1];
    return COLORS[paletteIndex];
}

/**
 * Gap between pie slices, in degrees. A fixed gap per slice lets a long tail
 * of tiny slices eat most of the circle and visually shrink the large ones,
 * so the total gap is capped at 18 degrees (5% of the pie).
 */
export function pieSlicePadding(sliceCount: number): number {
    return sliceCount > 1 ? Math.min(2, 18 / sliceCount) : 0;
}

export function groupDistributionItems(
    sortedData: DistributionItem[],
    total: number,
    selectedSet: Set<string>,
    labelKey: (label: string) => string,
    grouping?: SliceGrouping,
): DistributionItem[] {
    if (!grouping || total <= 0) return [...sortedData];
    if (sortedData.length <= (grouping.minSliceCount ?? 0)) return [...sortedData];

    const share = (item: DistributionItem) => (item.value / total) * 100;
    const isCandidate = (item: DistributionItem) =>
        share(item) < grouping.minSlicePercent && !selectedSet.has(labelKey(item.label));
    const majorSlices = sortedData.filter((item) => !isCandidate(item));
    // sortedData is descending, so candidates stay largest-first.
    const candidates = sortedData.filter(isCandidate);

    let groupedValue = candidates.reduce((sum, item) => sum + item.value, 0);
    let groupedCount = candidates.length;
    // Restore the largest candidates until the residual respects its ceiling.
    // A lone leftover is never worth hiding behind an "Other" label.
    while (
        groupedCount > 0 &&
        ((groupedValue / total) * 100 > grouping.maxOtherPercent || groupedCount === 1)
    ) {
        const restored = candidates[candidates.length - groupedCount];
        majorSlices.push(restored);
        groupedValue -= restored.value;
        groupedCount -= 1;
    }

    if (groupedCount === 0) return [...sortedData];

    majorSlices.sort((a, b) => b.value - a.value);
    return [...majorSlices, { label: "Other", value: groupedValue, isGroupedOther: true }];
}

export function DistributionChart({
    title,
    helpText,
    data,
    selectedValue,
    valueSeparator = ",",
    onSegmentClick,
    legendPosition = "right",
    showTotal = true,
    totalOverride,
    formatLabel,
    matchLabel,
    wrapLegendLabels = false,
    grouping,
}: DistributionChartProps) {
    const [groupSmallSlices, setGroupSmallSlices] = useState(true);
    const displayLabel = formatLabel ?? ((label: string) => label);
    const labelKey = matchLabel ?? formatLabel ?? ((label: string) => label);
    const total = data.reduce((sum, item) => sum + item.value, 0);
    const pendingEntryRef = useRef<{ name: string; isGroupedOther?: boolean } | null>(null);
    const { pulseKey, triggerPulse } = useLongPressPulse();
    const fireLongPress = useCallback(() => {
        const entry = pendingEntryRef.current;
        if (entry && onSegmentClick) {
            if (entry.isGroupedOther) return false;
            const name = entry.name;
            return () => onSegmentClick(name, true);
        }
        return false;
    }, [onSegmentClick]);
    const { pressHandlers, consumeLongPress, consumeSuppressClick } = useLongPressMultiSelect({
        onLongPressRecognized: () => {
            if (pendingEntryRef.current) triggerPulse(pendingEntryRef.current.name);
        },
        onLongPress: fireLongPress,
    });
    const piePressHandlers = onSegmentClick
        ? {
              onPointerDown: (
                  data: { name?: string | number; isGroupedOther?: boolean },
                  _index: number,
                  e: React.PointerEvent,
              ) => {
                  e.stopPropagation();
                  if (data?.name != null) {
                      pendingEntryRef.current = { name: String(data.name), isGroupedOther: data.isGroupedOther };
                  }
                  pressHandlers.onPointerDown(e);
              },
              onPointerMove: (_data: unknown, _index: number, e: React.PointerEvent) => {
                  pressHandlers.onPointerMove(e);
              },
              onPointerUp: (_data: unknown, _index: number, e: React.PointerEvent) => {
                  pressHandlers.onPointerUp(e);
              },
              onPointerCancel: (_data: unknown, _index: number, e: React.PointerEvent) => {
                  pendingEntryRef.current = null;
                  pressHandlers.onPointerCancel(e);
              },
          }
        : {};
    const clickHint = onSegmentClick ? ` — ${MULTI_SELECT_HINT_TITLE.toLowerCase()}` : "";

    // Smart percentage formatting: show enough decimals to avoid misleading "0%"
    const fmtPct = (pct: number) => {
        if (pct === 0) return "0%";
        if (pct >= 1) return `${pct.toFixed(0)}%`;
        if (pct >= 0.1) return `${pct.toFixed(1)}%`;
        return "<0.1%";
    };

    // Support multi-select highlighting (split by valueSeparator)
    const selectedSet = useMemo(() => {
        if (!selectedValue) return new Set<string>();
        return new Set(
            selectedValue
                .split(valueSeparator)
                .map((s) => s.trim())
                .filter(Boolean)
                .map(labelKey),
        );
    }, [selectedValue, valueSeparator, labelKey]);
    const hasSelection = selectedSet.size > 0;
    // Center total: use override if provided, else sum of selected segments, else full total
    const displayTotal = totalOverride != null
        ? totalOverride
        : hasSelection
            ? data.filter((item) => selectedSet.has(labelKey(item.label))).reduce((sum, item) => sum + item.value, 0)
            : total;

    // Sort data by value (descending) for consistent display
    const sortedData = [...data].sort((a, b) => b.value - a.value);

    const groupedData = groupDistributionItems(sortedData, total, selectedSet, labelKey, grouping);
    // Keep the toggle available when selection happens to expose the last hidden slice.
    const canGroup = grouping != null && (
        groupedData.some((item) => item.isGroupedOther) ||
        groupDistributionItems(sortedData, total, new Set(), labelKey, grouping).some((item) => item.isGroupedOther)
    );
    const displayData = groupSmallSlices ? groupedData : sortedData;
    const groupedCount =
        sortedData.length - displayData.filter((item) => !item.isGroupedOther).length;

    const chartData = displayData.map((item, index) => ({
        name: item.label,
        displayName: displayLabel(item.label),
        value: item.value,
        percentage: item.percentage ?? (total > 0 ? (item.value / total) * 100 : 0),
        color: sliceColor(index, displayData.length, item.isGroupedOther ?? false),
        isSelected: selectedSet.has(labelKey(item.label)),
        isGroupedOther: item.isGroupedOther ?? false,
    }));

    const handleClick = (entry: { name: string; isGroupedOther?: boolean }, event?: React.MouseEvent) => {
        if (!onSegmentClick) return;
        if (entry.isGroupedOther) return;
        if (consumeSuppressClick()) return;
        onSegmentClick(entry.name, isMultiSelectClick(event, consumeLongPress()), event);
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h3 className={styles.title}>
                    <span title={`Distribution of ${title.toLowerCase()} across combinations${clickHint}`}>
                        {title}
                    </span>
                    {helpText ? <HelpHint text={helpText} /> : null}
                </h3>
                {canGroup && (
                    <label className={styles.groupToggle}>
                        <input
                            type="checkbox"
                            checked={groupSmallSlices}
                            onChange={(event) => setGroupSmallSlices(event.target.checked)}
                        />
                        Group small slices
                    </label>
                )}
            </div>
            <div
                className={`${styles.content} ${legendPosition === "bottom" ? styles.contentVertical : ""}`}
                {...(onSegmentClick ? CONTEXT_MENU_BLOCKERS : {})}
            >
                <div className={`${styles.chartWrapper} ${onSegmentClick ? styles.interactive : ""}`}>
                    <ResponsiveContainer width="100%" height={160}>
                        <PieChart>
                            <Pie
                                data={chartData}
                                cx="50%"
                                cy="50%"
                                innerRadius={35}
                                outerRadius={60}
                                paddingAngle={pieSlicePadding(chartData.length)}
                                dataKey="value"
                                isAnimationActive={!onSegmentClick}
                                onClick={(data, _index, event) =>
                                    handleClick(
                                        { name: data.name, isGroupedOther: data.isGroupedOther },
                                        event as unknown as React.MouseEvent,
                                    )
                                }
                                style={{ cursor: onSegmentClick ? "pointer" : "default" }}
                                {...piePressHandlers}
                            >
                                {chartData.map((entry, index) => {
                                    const pulsing = pulseKey === entry.name;
                                    const baseOpacity = hasSelection && !entry.isSelected ? 0.4 : 1;
                                    const showPulseStroke = pulsing && !entry.isSelected;
                                    return (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={entry.color}
                                            opacity={longPressPulseOpacity(baseOpacity, pulsing)}
                                            stroke={
                                                entry.isSelected
                                                    ? "rgb(var(--colour-neutral-7, 17, 17, 17))"
                                                    : showPulseStroke
                                                      ? "rgba(var(--colour-neutral-7, 17, 17, 17), 0.55)"
                                                      : "none"
                                            }
                                            strokeWidth={entry.isSelected ? 2 : showPulseStroke ? 1 : 0}
                                        />
                                    );
                                })}
                                {showTotal && (
                                    <Label
                                        value={displayTotal.toLocaleString()}
                                        position="center"
                                        style={{ fontSize: "14px", fontWeight: 600, fill: "rgb(var(--colour-neutral-7, 17, 17, 17))" }}
                                    />
                                )}
                            </Pie>
                            <Tooltip
                                content={({ active, payload }) => {
                                    if (!active || !payload?.[0]) return null;
                                    const { displayName, value, isGroupedOther } = payload[0].payload;
                                    return (
                                        <div className={styles.tooltip}>
                                            <div>
                                                {displayName}: {value.toLocaleString()} ({fmtPct((value / total) * 100)})
                                            </div>
                                            {isGroupedOther && (
                                                <div className={styles.tooltipNote}>
                                                    {groupedCount} smallest values grouped together
                                                </div>
                                            )}
                                        </div>
                                    );
                                }}
                                position={{ x: 0, y: 0 }}
                                wrapperStyle={{ zIndex: 10, pointerEvents: "none" }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div
                    className={`${styles.legend} ${legendPosition === "bottom" ? styles.legendHorizontal : ""}`}
                    {...(onSegmentClick ? CONTEXT_MENU_BLOCKERS : {})}
                >
                    {chartData.map((item, index) => (
                        <div
                            key={index}
                            className={`${styles.legendItem} ${onSegmentClick ? styles.clickable : ""} ${item.isSelected ? styles.selected : ""} ${pulseKey === item.name ? gestureStyles.longPressPulse : ""}`}
                            onClick={(e) => handleClick(item, e)}
                            {...(onSegmentClick
                                ? mergeLongPressHandlers(
                                      pressHandlers,
                                      () => {
                                          pendingEntryRef.current = item;
                                      },
                                      () => {
                                          pendingEntryRef.current = null;
                                      },
                                  )
                                : {})}
                            style={{
                                opacity: longPressPulseOpacity(
                                    hasSelection && !item.isSelected ? 0.5 : 1,
                                    pulseKey === item.name,
                                ),
                            }}
                            title={`${item.displayName}: ${item.value.toLocaleString()} combinations (${fmtPct(item.percentage)})${
                                item.isGroupedOther ? " — remaining smaller slices grouped together" : ""
                            }${onSegmentClick && !item.isGroupedOther ? ` — ${MULTI_SELECT_HINT_TITLE.toLowerCase()}` : ""}`}
                        >
                            <span className={styles.legendColor} style={{ backgroundColor: item.color }} />
                            <span className={`${styles.legendLabel} ${wrapLegendLabels ? styles.legendLabelWrapped : ""}`}>
                                {item.displayName}
                            </span>
                            <span className={styles.legendValue}>{fmtPct(item.percentage)}</span>
                        </div>
                    ))}
                    {legendPosition !== "bottom" && (
                        <div className={styles.legendTotal}>
                            <span className={styles.legendLabel}>Total</span>
                            <span className={styles.legendValue}>{displayTotal.toLocaleString()}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
